import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { Button } from './ui/button';
import { ArrowUpDown, TrendingUp, TrendingDown, HelpCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const ITEMS_PER_PAGE = 50;
const PRICE_CACHE_TTL = 900000; // 15 minutes in ms (matches backend)

// Bulk price cache - stores entire response
let bulkPriceCache = {
  data: null,
  timestamp: 0,
  symbols: []
};

// Sparkline Y-domain cache - stores sticky min/max per symbol
const sparklineDomainCache = new Map();

// Auth check callback - can be overridden by parent
let onStockClickCallback = null;

export const setOnStockClickCallback = (callback) => {
  onStockClickCallback = callback;
};

// Fixed sparkline configuration
const SPARKLINE_CONFIG = {
  width: 80,
  height: 28,
  points: 20,       // Fixed number of points
  padding: 3,       // Vertical padding in pixels
  strokeWidth: 1.5
};

// Generate stable sparkline data for a symbol (seeded pseudo-random based on symbol)
const generateStableSparklineData = (symbol, positive) => {
  // Create a simple hash from symbol for consistent "randomness"
  let hash = 0;
  for (let i = 0; i < symbol.length; i++) {
    hash = ((hash << 5) - hash) + symbol.charCodeAt(i);
    hash = hash & hash;
  }
  
  const points = [];
  let value = 50 + (hash % 20);
  const trend = positive ? 0.3 : -0.3;
  
  for (let i = 0; i < SPARKLINE_CONFIG.points; i++) {
    // Seeded variation based on symbol hash and position
    const variation = ((hash * (i + 1)) % 100) / 100 - 0.5;
    value += variation * 3 + trend;
    value = Math.max(20, Math.min(80, value)); // Clamp to prevent extremes
    points.push(value);
  }
  
  return points;
};

// Stable Mini Sparkline Component
const Sparkline = ({ symbol, positive }) => {
  const { width, height, padding, strokeWidth } = SPARKLINE_CONFIG;
  const color = positive ? '#4ade80' : '#f87171';
  
  // Generate stable data based on symbol (memoized)
  const points = useMemo(() => {
    return generateStableSparklineData(symbol, positive);
  }, [symbol, positive]);
  
  // Get or create sticky Y domain for this symbol
  const { yMin, yMax } = useMemo(() => {
    const cached = sparklineDomainCache.get(symbol);
    const currentMin = Math.min(...points);
    const currentMax = Math.max(...points);
    
    // If cached, only expand domain (never shrink)
    if (cached) {
      const newMin = Math.min(cached.yMin, currentMin);
      const newMax = Math.max(cached.yMax, currentMax);
      sparklineDomainCache.set(symbol, { yMin: newMin, yMax: newMax });
      return { yMin: newMin, yMax: newMax };
    }
    
    // First time: set initial domain with padding
    const range = currentMax - currentMin || 1;
    const paddedMin = currentMin - range * 0.1;
    const paddedMax = currentMax + range * 0.1;
    sparklineDomainCache.set(symbol, { yMin: paddedMin, yMax: paddedMax });
    return { yMin: paddedMin, yMax: paddedMax };
  }, [symbol, points]);
  
  // Calculate Y range with protection against division by zero
  const yRange = yMax - yMin || 1;
  const drawHeight = height - padding * 2;
  
  // Build linear path with fixed X spacing
  const pathData = useMemo(() => {
    return points.map((val, i) => {
      const x = (i / (points.length - 1)) * width;
      const normalizedY = (val - yMin) / yRange;
      const y = padding + drawHeight - (normalizedY * drawHeight);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    }).join(' ');
  }, [points, yMin, yRange, width, drawHeight, padding]);
  
  return (
    <div 
      style={{ 
        width: `${width}px`, 
        height: `${height}px`,
        flexShrink: 0,
        overflow: 'hidden'
      }}
    >
      <svg 
        width={width} 
        height={height} 
        style={{ display: 'block' }}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
      >
        <path 
          d={pathData} 
          fill="none" 
          stroke={color} 
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
};

// Column tooltip helper
const ColumnHelp = ({ text }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle className="h-3.5 w-3.5 ml-1 opacity-40 hover:opacity-100 cursor-help" style={{ color: '#94A3B8' }} />
      </TooltipTrigger>
      <TooltipContent style={{ backgroundColor: '#0E1324', border: '1px solid rgba(59, 130, 246, 0.15)' }}>
        <p className="text-xs max-w-[200px]" style={{ color: '#E2E8F0' }}>{text}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const AdvancedTable = ({ data, onRowClick }) => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState('unusual_activity_score');
  const [sortDirection, setSortDirection] = useState('desc');
  const [prices, setPrices] = useState({});
  const [pricesLoading, setPricesLoading] = useState(true);
  const [priceUpdates, setPriceUpdates] = useState({});
  const isVisibleRef = useRef(true);
  const fetchingRef = useRef(false);

  // Handle row click - use callback if provided, otherwise navigate directly
  const handleRowClick = (symbol) => {
    if (onRowClick) {
      onRowClick(symbol);
    } else {
      navigate(`/app/stock/${symbol}`);
    }
  };

  // Fetch bulk prices for all symbols at once
  const fetchBulkPrices = useCallback(async (symbols) => {
    if (!symbols || symbols.length === 0) {
      setPricesLoading(false);
      return;
    }
    if (fetchingRef.current) return;
    
    // Filter out any undefined/null symbols
    const validSymbols = symbols.filter(s => s && typeof s === 'string');
    if (validSymbols.length === 0) {
      setPricesLoading(false);
      return;
    }
    
    // Check if cache is still valid
    const now = Date.now();
    const cacheValid = bulkPriceCache.data && 
      (now - bulkPriceCache.timestamp) < PRICE_CACHE_TTL &&
      validSymbols.every(s => bulkPriceCache.symbols.includes(s));
    
    if (cacheValid) {
      // Use cached data
      const cachedPrices = {};
      validSymbols.forEach(symbol => {
        if (bulkPriceCache.data.quotes && bulkPriceCache.data.quotes[symbol]) {
          const q = bulkPriceCache.data.quotes[symbol];
          cachedPrices[symbol] = {
            price: q.price,
            change: q.change,
            changePercent: q.changePercent,
            source: q.source,  // Pass through source for market closed detection
            lastUpdate: new Date(bulkPriceCache.data.asOf),
            marketStatus: bulkPriceCache.data.marketStatus || { isOpen: false },
            updatedAt: new Date(bulkPriceCache.data.asOf)
          };
        }
      });
      setPrices(cachedPrices);
      setPricesLoading(false);
      return;
    }
    
    fetchingRef.current = true;
    setPricesLoading(true);
    
    try {
      const response = await axios.post(`${API_BASE}/api/quotes/bulk`, {
        symbols: validSymbols
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const { quotes, asOf, marketStatus } = response.data;
      
      // Update cache
      bulkPriceCache = {
        data: response.data,
        timestamp: Date.now(),
        symbols: validSymbols
      };
      
      // Transform to price format expected by component
      const newPrices = {};
      Object.entries(quotes || {}).forEach(([symbol, q]) => {
        newPrices[symbol] = {
          price: q.price,
          change: q.change,
          changePercent: q.changePercent,
          source: q.source,  // Pass through source for market closed detection
          lastUpdate: new Date(asOf),
          marketStatus: marketStatus || { isOpen: false },
          updatedAt: new Date(asOf)
        };
      });
      
      setPrices(newPrices);
      setPriceUpdates(prev => {
        const updates = {};
        validSymbols.forEach(s => { updates[s] = Date.now(); });
        return { ...prev, ...updates };
      });
    } catch (error) {
      console.error('Error fetching bulk prices:', error);
    } finally {
      fetchingRef.current = false;
      setPricesLoading(false);
    }
  }, []);

  // Visibility tracking
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Fetch prices for all visible stocks using bulk endpoint
  useEffect(() => {
    if (!data.length || !isVisibleRef.current) return;
    
    const symbols = data.map(s => s.symbol);
    
    // Initial fetch
    fetchBulkPrices(symbols);
    
    // Refresh every 15 minutes (matching backend cache TTL)
    const interval = setInterval(() => {
      if (isVisibleRef.current) {
        bulkPriceCache = { data: null, timestamp: 0, symbols: [] }; // Clear cache
        fetchBulkPrices(symbols);
      }
    }, PRICE_CACHE_TTL);
    
    return () => clearInterval(interval);
  }, [data, fetchBulkPrices]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    
    if (typeof aVal === 'number') {
      return (aVal - bVal) * multiplier;
    }
    return String(aVal).localeCompare(String(bVal)) * multiplier;
  });

  // Get "How Unusual" label and color based on z-score
  const getUnusualLabel = (zscore, volumeMultiple) => {
    const absZ = Math.abs(zscore);
    
    if (volumeMultiple >= 5) {
      return { label: 'VERY HIGH', color: '#f87171', bg: 'rgba(248, 113, 113, 0.15)' };
    }
    if (absZ >= 4) {
      return { label: 'Very Rare Move', color: '#f87171', bg: 'rgba(248, 113, 113, 0.15)' };
    }
    if (absZ >= 3) {
      return { label: 'Rare Move', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.15)' };
    }
    if (absZ >= 2) {
      return { label: 'Moderate Move', color: '#4ade80', bg: 'rgba(74, 222, 128, 0.15)' };
    }
    if (volumeMultiple >= 2) {
      return { label: 'Elevated', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.15)' };
    }
    return { label: 'Notable', color: '#a1a1aa', bg: 'rgba(161, 161, 170, 0.15)' };
  };

  // Get activity level label
  const getActivityLabel = (score) => {
    if (score >= 75) return { label: 'High', color: '#f87171' };
    if (score >= 50) return { label: 'Moderate', color: '#fbbf24' };
    if (score >= 25) return { label: 'Elevated', color: '#4ade80' };
    return { label: 'Normal', color: '#a1a1aa' };
  };

  // Format time since update
  const getTimeSince = (lastUpdate) => {
    if (!lastUpdate) return 'Loading...';
    const seconds = Math.floor((Date.now() - lastUpdate) / 1000);
    if (seconds < 60) return `Last ${seconds} sec`;
    const minutes = Math.floor(seconds / 60);
    return `Last ${minutes} min`;
  };

  // Format update time in local HH:MM format
  const formatUpdateTime = (date) => {
    if (!date) return 'just now';
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="overflow-x-auto" data-testid="anomaly-table-container">
      <Table>
        <TableHeader style={{ backgroundColor: 'rgba(11, 15, 26, 0.8)' }}>
          <TableRow style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
            {/* STOCK */}
            <TableHead className="w-[180px]">
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  onClick={() => handleSort('symbol')}
                  className="text-xs uppercase tracking-wider hover:bg-white/5 px-2 font-medium h-8"
                  style={{ color: '#94A3B8' }}
                  data-testid="sort-stock"
                >
                  Stock
                  <ArrowUpDown className="ml-1 h-3 w-3" />
                </Button>
              </div>
            </TableHead>
            
            {/* WHEN */}
            <TableHead className="w-[100px]">
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  onClick={() => handleSort('date')}
                  className="text-xs uppercase tracking-wider hover:bg-white/5 px-2 font-medium h-8"
                  style={{ color: '#94A3B8' }}
                  data-testid="sort-when"
                >
                  When
                  <ArrowUpDown className="ml-1 h-3 w-3" />
                </Button>
                <ColumnHelp text="The date when this unusual activity was detected" />
              </div>
            </TableHead>
            
            {/* PRICE CHANGE */}
            <TableHead className="w-[140px]">
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  onClick={() => handleSort('daily_return')}
                  className="text-xs uppercase tracking-wider hover:bg-white/5 px-2 font-medium h-8"
                  style={{ color: '#94A3B8' }}
                  data-testid="sort-change"
                >
                  Price Change
                  <ArrowUpDown className="ml-1 h-3 w-3" />
                </Button>
              </div>
            </TableHead>
            
            {/* DELAYED PRICE / LAST PRICE - based on market status */}
            <TableHead className="w-[150px]">
              <div className="flex items-center">
                <span className="text-xs uppercase tracking-wider px-2 font-medium flex items-center" style={{ color: '#94A3B8' }}>
                  {Object.values(prices)[0]?.marketStatus?.isOpen ? 'Delayed Price' : 'Last Price'}
                  <span 
                    className="ml-1.5 text-[9px] font-normal px-1 py-0.5 rounded" 
                    style={{ 
                      backgroundColor: Object.values(prices)[0]?.marketStatus?.isOpen ? 'rgba(251, 191, 36, 0.15)' : 'rgba(239, 68, 68, 0.15)', 
                      color: Object.values(prices)[0]?.marketStatus?.isOpen ? '#fbbf24' : '#ef4444' 
                    }}
                  >
                    {Object.values(prices)[0]?.marketStatus?.isOpen ? '15m' : 'LAST'}
                  </span>
                </span>
              </div>
            </TableHead>
            
            {/* HOW UNUSUAL */}
            <TableHead className="w-[160px]">
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  onClick={() => handleSort('return_zscore')}
                  className="text-xs uppercase tracking-wider hover:bg-white/5 px-2 font-medium h-8"
                  style={{ color: '#94A3B8' }}
                  data-testid="sort-unusual"
                >
                  How Unusual
                  <ArrowUpDown className="ml-1 h-3 w-3" />
                </Button>
                <ColumnHelp text="Statistical measure of how unusual this move is compared to recent history" />
              </div>
            </TableHead>
            
            {/* ACTIVITY SCORE */}
            <TableHead className="w-[150px]">
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  onClick={() => handleSort('unusual_activity_score')}
                  className="text-xs uppercase tracking-wider hover:bg-white/5 px-2 font-medium h-8"
                  style={{ color: '#94A3B8' }}
                  data-testid="sort-score"
                >
                  Activity Score
                  <ArrowUpDown className="ml-1 h-3 w-3" />
                </Button>
                <ColumnHelp text="Combined score (0-100) measuring overall unusual activity" />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        
        <TableBody>
          {sortedData.map((stock, index) => {
            const priceData = prices[stock.symbol];
            const unusualInfo = getUnusualLabel(stock.return_zscore, stock.volume_multiple);
            const activityInfo = getActivityLabel(stock.unusual_activity_score);
            const isPositive = stock.daily_return >= 0;
            
            return (
              <TableRow
                key={stock.symbol}
                className="cursor-pointer transition-colors group"
                style={{ 
                  borderColor: 'rgba(255,255,255,0.04)',
                  backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)'}
                onClick={() => handleRowClick(stock.symbol)}
                data-testid={`stock-row-${stock.symbol}`}
                data-tour-stock-row="true"
                data-symbol={stock.symbol}
              >
                {/* STOCK - Boxed Card Module */}
                <TableCell className="py-3 pr-2">
                  <div 
                    className="inline-flex flex-col rounded-lg"
                    style={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.025)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      padding: '10px 12px',
                      width: '148px'
                    }}
                  >
                    {/* Ticker - Bold */}
                    <div className="font-mono font-bold text-sm tracking-tight" style={{ color: '#FAFAFA' }}>
                      {stock.symbol}
                    </div>
                    
                    {/* Company Name - Muted */}
                    {stock.company_name && stock.company_name !== stock.symbol && (
                      <div 
                        className="text-xs truncate mt-0.5" 
                        style={{ color: '#71717a', maxWidth: '124px' }}
                      >
                        {stock.company_name}
                      </div>
                    )}
                    
                    {/* Sparkline - Fixed dimensions, stable */}
                    <div className="mt-2" style={{ width: '80px', height: '28px', overflow: 'hidden' }}>
                      <Sparkline symbol={stock.symbol} positive={isPositive} />
                    </div>
                  </div>
                </TableCell>
                
                {/* WHEN */}
                <TableCell className="py-3">
                  <span className="text-sm" style={{ color: '#a1a1aa' }}>
                    {formatDate(stock.date)}
                  </span>
                </TableCell>
                
                {/* PRICE CHANGE */}
                <TableCell className="py-3">
                  <div className="flex items-center gap-2">
                    {isPositive ? (
                      <TrendingUp className="h-4 w-4" style={{ color: '#4ade80' }} />
                    ) : (
                      <TrendingDown className="h-4 w-4" style={{ color: '#f87171' }} />
                    )}
                    <span 
                      className="font-mono font-semibold text-sm" 
                      style={{ color: isPositive ? '#4ade80' : '#f87171' }}
                    >
                      {isPositive ? '+' : ''}{stock.daily_return.toFixed(2)}%
                    </span>
                  </div>
                </TableCell>
                
                {/* DELAYED PRICE / LAST PRICE - based on source */}
                <TableCell className="py-3">
                  {priceData ? (
                    <div>
                      <div className="flex items-center gap-1.5">
                        {/* Status indicator - amber for market open, red for market closed */}
                        <span 
                          className="w-2 h-2 rounded-full flex-shrink-0" 
                          style={{ 
                            backgroundColor: priceData.marketStatus?.isOpen !== false
                              ? '#f59e0b' 
                              : '#ef4444' 
                          }}
                        />
                        {/* Price - larger and bolder */}
                        <span className="font-mono text-base font-semibold" style={{ color: '#FAFAFA' }}>
                          {priceData.price?.toFixed(2)}
                        </span>
                        {/* Currency - smaller and muted */}
                        <span className="text-xs font-normal" style={{ color: '#71717a' }}>
                          USD
                        </span>
                      </div>
                      {/* Status text - show appropriate message based on market status */}
                      <div className="text-[10px] mt-0.5 ml-3.5" style={{ color: '#71717a' }}>
                        {priceData.marketStatus?.isOpen !== false 
                          ? '15 min delayed' 
                          : 'last price'}
                      </div>
                    </div>
                  ) : (
                    <div className="animate-pulse">
                      <div className="h-5 w-20 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }} />
                    </div>
                  )}
                </TableCell>
                
                {/* HOW UNUSUAL */}
                <TableCell className="py-3">
                  <div>
                    <span 
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{ backgroundColor: unusualInfo.bg, color: unusualInfo.color }}
                    >
                      {unusualInfo.label}
                    </span>
                    <div className="text-xs mt-1 font-mono" style={{ color: '#52525b' }}>
                      {Math.abs(stock.return_zscore).toFixed(1)}σ
                    </div>
                  </div>
                </TableCell>
                
                {/* ACTIVITY SCORE */}
                <TableCell className="py-3">
                  <div>
                    <div className="text-sm font-mono" style={{ color: '#a1a1aa' }}>
                      {stock.unusual_activity_score.toFixed(0)} <span style={{ color: '#52525b' }}>/ 100</span>
                    </div>
                    <span 
                      className="px-2 py-0.5 rounded text-xs font-medium mt-1 inline-block"
                      style={{ backgroundColor: `${activityInfo.color}15`, color: activityInfo.color }}
                    >
                      {activityInfo.label}
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      
      {/* Bottom hint */}
      <div className="px-4 py-3 text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <p className="text-[11px]" style={{ color: '#52525b' }}>
          Click any row to see detailed analysis
        </p>
      </div>
    </div>
  );
};

export default AdvancedTable;
